import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  CircularProgress,
  Flex,
  useToast
} from "@chakra-ui/react";
import {SortableContainer, SortableContainerProps, SortableElement, SortableElementProps} from "react-sortable-hoc";
import {useRef, useState} from "react";
import {arrayMoveImmutable} from "array-move";
import {ItemType} from "../services/types";
import {Card} from "./Card";

export interface BookingOptions {
  /** Whether booking is available in this wishlist at all. */
  enabled: boolean;
  /** The viewer owns this wishlist (owners never book their own items). */
  isOwner: boolean;
  onBook: (uuid: string) => Promise<unknown>;
  onUnbook: (uuid: string) => Promise<unknown>;
}

interface CardBookingProps {
  booked?: boolean;
  bookedByMe?: boolean;
  bookedBy?: string | null;
  canBook?: boolean;
  canUnbook?: boolean;
  bookingBusy?: boolean;
  onBook?: () => void;
  onUnbook?: () => void;
}

interface SortableItemProps extends SortableElementProps {
  value: ItemType;
  showHandle: boolean;
  showEditButton: boolean;
  booking: CardBookingProps;
}

interface SortableListProps extends SortableContainerProps {
  items: ItemType[];
  disabled: boolean;
  bookingProps: (item: ItemType) => CardBookingProps;
}

const SortableItem = SortableElement<SortableItemProps>(({value, showHandle, showEditButton, booking}: SortableItemProps) => {
  return <Card
    uuid={value.uuid}
    image={value.image || ''}
    title={value.name}
    description={value.description ?? undefined}
    link={value.link}
    hasHandle={showHandle}
    editable={showEditButton}
    {...booking}
  />;
});

const SortableList = SortableContainer<SortableListProps>(({items, disabled, bookingProps}: SortableListProps) => {
  return <Flex my={5} justifyContent="space-between" alignItems="stretch" flexWrap="wrap" gap={3}>
    {items.map((value: ItemType, index: number) => (
      <SortableItem key={value.uuid} value={value} index={index}
                    disabled={disabled} showHandle={!disabled} showEditButton={!disabled}
                    booking={bookingProps(value)}/>
    ))}
  </Flex>;
});

interface ItemsGridProps {
  items: ItemType[];
  setItems: (items: ItemType[]) => void;
  /** When true the owner can drag to reorder and edit items. */
  editable: boolean;
  /** Persist the new order (array of uuids, first = top). */
  persistSorting: (uuids: string[]) => Promise<unknown>;
  booking?: BookingOptions;
}

export const ItemsGrid = ({items, setItems, editable, persistSorting, booking}: ItemsGridProps) => {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [updateTimeout, setUpdateTimeout] = useState<NodeJS.Timeout | number | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);
  const [confirmUnbook, setConfirmUnbook] = useState<ItemType | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const runBooking = (uuid: string, action: (uuid: string) => Promise<unknown>) => {
    setBusyItem(uuid);
    action(uuid).finally(() => setBusyItem(null));
  };

  const bookingProps = (item: ItemType): CardBookingProps => {
    if (!booking) {
      return {};
    }

    const booked = !!item.booked;
    return {
      booked,
      bookedByMe: !!item.booked_by_me,
      bookedBy: item.booked_by,
      canBook: booking.enabled && !booking.isOwner,
      canUnbook: !!item.booked_by_me || (booking.isOwner && booked),
      bookingBusy: busyItem === item.uuid,
      onBook: () => runBooking(item.uuid, booking.onBook),
      onUnbook: () => setConfirmUnbook(item),
    };
  };

  const movable = editable && !saving;

  const onSortStart = () => {
    if (!movable) {
      return;
    }
    if (updateTimeout !== null) {
      clearTimeout(updateTimeout);
    }
  };

  const onSortEnd = ({oldIndex, newIndex}: { oldIndex: number, newIndex: number }) => {
    if (!movable) {
      return;
    }
    if (updateTimeout !== null) {
      clearTimeout(updateTimeout);
    }

    const reordered = arrayMoveImmutable(items, oldIndex, newIndex);
    setItems(reordered);
    setUpdateTimeout(setTimeout(() => {
      setSaving(true);
      const t = toast({
        position: 'top-right',
        duration: null,
        render: () => (
          <Box color="white" p={3} bg="green.300">
            <CircularProgress size="25px" isIndeterminate color="white" me={2}/>
            Saving...
          </Box>
        )
      });
      persistSorting(reordered.map(i => i.uuid))
        .finally(() => {
          toast.close(t);
          setSaving(false);
        });
    }, 750));
  };

  return <>
    <SortableList
      items={items}
      axis="xy"
      useDragHandle={true}
      onSortStart={onSortStart}
      onSortEnd={onSortEnd}
      disabled={!movable}
      disableAutoscroll={false}
      bookingProps={bookingProps}
    />

    <AlertDialog
      motionPreset="slideInBottom"
      isOpen={confirmUnbook !== null}
      onClose={() => setConfirmUnbook(null)}
      isCentered
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay/>
      <AlertDialogContent>
        <AlertDialogHeader>Cancel booking?</AlertDialogHeader>
        <AlertDialogCloseButton/>
        <AlertDialogBody>
          {confirmUnbook?.booked_by_me
            ? <>Are you sure you want to cancel your booking of <b>{confirmUnbook?.name}</b>? Someone else will be able
              to book it.</>
            : <>Are you sure you want to cancel the booking of <b>{confirmUnbook?.name}</b>?</>}
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button ref={cancelRef} onClick={() => setConfirmUnbook(null)}>Keep it</Button>
          <Button colorScheme="red" ml={3} onClick={() => {
            const item = confirmUnbook;
            setConfirmUnbook(null);
            if (item && booking) {
              runBooking(item.uuid, booking.onUnbook);
            }
          }}>Cancel booking</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
};
